import { FC, useEffect, useState } from 'react';
import { LineProgress } from 'src/components/progressBars';
import { Grid, Title } from '@mantine/core';
import { usageCharts } from 'src/constants/usageCharts';
import { useTranslation } from 'react-i18next';
import { LineProgressProps } from '@common/types/types';
import { findMaxValue, sortByValueDescending } from 'src/helpers/mathOperations';

import styles from './styles.module.scss';
import classNames from 'classnames';
import { BrowserUsage, DevicesUsage, IReportedIssueDashboard, OperatingSystemUsage, SystemUsage } from 'src/store';

interface UsageChartProps {
  title?: string;
  data: LineProgressProps[];
  noShadow?: boolean;
  toBeSorted?: boolean;
  deviceWidth?: number;
  isPercent?: boolean;
}

export const UsageChart: FC<UsageChartProps> = ({
  data,
  title,
  noShadow,
  toBeSorted = true,
  deviceWidth,
  isPercent,
}) => {
  const [t] = useTranslation('reported');

  let sortedData = data;
  const maxValue = findMaxValue(data) || 1;

  if (toBeSorted) {
    sortedData = sortByValueDescending(data);
  }

  return (
    <div className={classNames(styles['usage-charts'], { [styles.noShadow]: noShadow })}>
      {title && (
        <Title order={2} size={18} weight={700} color="#2A2B4B" pb={24}>
          {t(title)}
        </Title>
      )}

      {sortedData.map((item) => (
        <LineProgress
          color={item?.color}
          isPercent={isPercent}
          className={styles['line-progress']}
          key={item.label}
          label={t(item.label)}
          percent={(item.value / maxValue) * 100}
          value={item.value}
          deviceWidth={deviceWidth}
          
        />
      ))}
    </div>
  );
};

interface UsageChartsProps {
  data: IReportedIssueDashboard | null;
}

export const UsageCharts: FC<UsageChartsProps> = ({ data }) => {
  const [chartData, setChartData] = useState<UsageChartProps[] | []>([]);

  useEffect(() => {
    if (data) {
      const newChartData = usageCharts.map((e) => {
        const newElem: LineProgressProps[] = e.data.map((el) => {
          return {
            label: el.label,
            value:
              data?.[e.field as keyof IReportedIssueDashboard]?.[
                el.field as keyof (BrowserUsage | OperatingSystemUsage | DevicesUsage | SystemUsage)
              ] ?? 0,
          };
        });

        return Object.assign({}, { title: e.title }, { data: newElem });
      });

      setChartData(newChartData);
    }
  }, [data]);

  return (
    <Grid className={styles.charts} gutter={16} my={16} w={'100%'}>
      {chartData.map((chart) => (
        <Grid.Col my={6} span={12} lg={4} key={chart.title}>
          <UsageChart title={chart.title} data={chart.data} deviceWidth={220} />
        </Grid.Col>
      ))}
    </Grid>
  );
};

export default UsageCharts;
